import React from 'react'
import type { ReactNode } from 'react'
import { cn } from "@/lib/utils"

interface TabsProps {
  value: string
  onValueChange: (value: string) => void
  className?: string
  children: ReactNode
}

interface TabsListProps {
  className?: string
  children: ReactNode
  currentValue?: string
  onValueChange?: (value: string) => void
}

interface TabsTriggerProps {
  value: string
  children: ReactNode
  className?: string
  disabled?: boolean
  currentValue?: string
  onValueChange?: (value: string) => void
}

interface TabsContentProps {
  value: string
  children: ReactNode
  className?: string
  currentValue?: string
}

const Tabs: React.FC<TabsProps> = ({ value, onValueChange, className, children }) => {
  return (
    <div className={className}>
      {React.Children.map(children, (child) =>
        React.cloneElement(child as React.ReactElement<any>, { currentValue: value, onValueChange })
      )}
    </div>
  )
}

const TabsList: React.FC<TabsListProps> = ({ className, children, currentValue, onValueChange }) => {
  return (
    <div
      className={cn(
        "inline-flex items-center justify-center rounded-full bg-secondary p-1.5 text-muted-foreground gap-1",
        className
      )}
      role="tablist"
    >
      {React.Children.map(children, (child) => {
        const childEl = child as React.ReactElement<any>
        return React.cloneElement(childEl, {
          currentValue,
          onValueChange,
        })
      })}
    </div>
  )
}

const TabsTrigger: React.FC<TabsTriggerProps> = ({ 
  value: triggerValue, 
  children, 
  className,
  disabled = false,
  currentValue,
  onValueChange,
}) => {
  const isActive = currentValue === triggerValue
  
  return (
    <button
      onClick={() => !disabled && onValueChange?.(triggerValue)}
      type="button"
      disabled={disabled}
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-semibold transition-colors duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:pointer-events-none disabled:opacity-50",
        isActive ? "bg-primary text-card" : "text-muted-foreground hover:text-foreground",
        className
      )}
      role="tab"
      aria-selected={isActive}
    >
      {children}
    </button>
  )
}

const TabsContent: React.FC<TabsContentProps> = ({ 
  value: contentValue, 
  children, 
  className,
  currentValue
}) => {
  if (currentValue !== contentValue) return null
  
  return (
    <div
      className={cn(
        "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className
      )}
      role="tabpanel"
    >
      {children}
    </div>
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent }